import { CheckOutlined, EditOutlined } from "@mui/icons-material";
import { Divider, IconButton, InputAdornment, SvgIcon, Table, TableBody, TableCell, TableHead, TableRow, TextField } from "@mui/material";
import Trash03Icon from "@untitled-ui/icons-react/build/esm/Trash03";
import PlusIcon from "@untitled-ui/icons-react/build/esm/Plus";
import MinusIcon from "@untitled-ui/icons-react/build/esm/Minus";
import Typography from "@mui/material/Typography";
import { useRouter } from "next/router";
import { FC, Fragment, useEffect, useMemo, useState } from "react";
import { useTillSummary } from "~/hooks/use-till-summary";
import { useGetFixDiscountsCache } from "~/queries/useGetFixDiscounts";
import { useGetAdditionalFeesCache } from "~/queries/useGetAdditionalFees";
import { UpdateFileVariables, useUpdateFile } from "~/queries/useUpdateFile";
import { useSetTillIsEditingFileInput, useTill, useUpdateTillCreateFileInput, useUpdateTillUpdateFileInput } from "~/store";
import { CreateFileInput, DISCOUNT_TYPE, FileProductInput } from "~/types/__generated/gql/graphql";
import { applyAdditionalFeeToFile, applyFixDiscountToFile, calculateFile } from "~served/utils";
import { AdditionalFeeInput, FileInputLike, FileProductInputLike, FileProductOptionInputLike, FixDiscountInput, LiteralValueOf } from "~served/utils/calculations/types";
import { $dayjs, DATETIMESTRING_FORMAT } from "~/libs/dayjs";
import { DEFAULT_CURRENCY_CODE } from "~served/configs";
import { $d } from "~/utils/currency-display";
import { useGetCurrentCompany } from "~/queries/useGetCurrentCompany";
export interface TableItemOption {
  id: string;
  name: string;
  quantity: number;
  subtotal: number;
}
export interface SubtotalAddonAdditionalFee {
  type: LiteralValueOf<DISCOUNT_TYPE>;
  value: number;
  amount: number;
  additionalFee?: string;
}
export interface SubtotalAddonFixDiscount {
  type: LiteralValueOf<DISCOUNT_TYPE>;
  value: number;
  amount: number;
  fixDiscount?: string;
}
export interface TableSubtotalAddonValue {
  amount: number;
}
export interface TableSubtotalAddons {
  additionalFee: SubtotalAddonAdditionalFee;
  adjustment: TableSubtotalAddonValue;
  discount: TableSubtotalAddonValue;
  fixDiscount: SubtotalAddonFixDiscount;
  vat: TableSubtotalAddonValue;
}
export interface TableItemDisplay {
  prev: TableItem[];
  add: TableItem[];
  remove: TableItem[];
}
export interface TableItem {
  id: string;
  name: string;
  quantity: number;
  subtotal: number;
  options: TableItemOption[];
  subtotalAddons: TableSubtotalAddons;
  updatedAt?: string;
}
export interface ProductsTableSummaryProps {
  subtotal: number;
  grandTotal: number;
  additionalFee: number;
  fixDiscount: number;
  items: TableItem[];
  itemsDisplay: TableItemDisplay;
  onRowClick?: (item: TableItem) => void;
  isUpdateOnAdditionalFeeChange?: boolean;
  isUpdateOnDiscountChange?: boolean;
  isAddProduct?: boolean;
  columnVisibility?: {
    number?: boolean;
    quantity?: boolean;
    description?: boolean;
    total?: boolean;
  };
}
export const ProductsTableSummary: FC<ProductsTableSummaryProps> = ({
  items,
  itemsDisplay,
  subtotal,
  additionalFee,
  fixDiscount,
  grandTotal,
  onRowClick,
  isUpdateOnAdditionalFeeChange,
  isUpdateOnDiscountChange,
  isAddProduct,
  columnVisibility
}) => {
  const {
    data: fixDiscounts = []
  } = useGetFixDiscountsCache();
  const {
    data: additionalFees = []
  } = useGetAdditionalFeesCache();
  const {
    data: company
  } = useGetCurrentCompany();
  const {
    query
  } = useRouter();
  const {
    createFileInput,
    updateFileInput
  } = useTill();
  const {
    createFileInputSummary,
    updateFileInputSummary
  } = useTillSummary();
  const updateCreateFileInput = useUpdateTillCreateFileInput();
  const updateUpdateFileInput = useUpdateTillUpdateFileInput();
  const setTillIsEditFileInput = useSetTillIsEditingFileInput();
  const updateFile = useUpdateFile();
  const [isEditingDiscount, setIsEditingDiscount] = useState(false);
  const [tmpDiscount, setTmpDiscount] = useState({
    type: DISCOUNT_TYPE.PERCENTAGE,
    value: 0
  });
  const [isEditingAdditionalFee, setIsEditingAdditionalFee] = useState(false);
  const [tmpAdditionalFee, setTmpAdditionalFee] = useState({
    type: DISCOUNT_TYPE.PERCENTAGE,
    value: 0
  });
  useEffect(() => {
    setTmpDiscount(isEditingDiscount ? {
      type: DISCOUNT_TYPE.AMOUNT,
      value: fixDiscount
    } : {
      type: DISCOUNT_TYPE.PERCENTAGE,
      value: 0
    });
    setTmpAdditionalFee(isEditingAdditionalFee ? {
      type: DISCOUNT_TYPE.AMOUNT,
      value: additionalFee
    } : {
      type: DISCOUNT_TYPE.PERCENTAGE,
      value: 0
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditingDiscount, isEditingAdditionalFee]);
  const isAllApplyFixeDiscount = useMemo(() => {
    if (createFileInput) return createFileInputSummary.$isAllProductsWithFixDiscount;
    if (updateFileInput) return updateFileInputSummary.$isAllProductsWithFixDiscount;
    return true;
  }, [createFileInput, createFileInputSummary, updateFileInput, updateFileInputSummary]);
  const isAllApplyAdditionalFee = useMemo(() => {
    if (createFileInput) return createFileInputSummary.$isAllProductsWithAdditionalFee;
    if (updateFileInput) return updateFileInputSummary.$isAllProductsWithAdditionalFee;
    return true;
  }, [createFileInput, createFileInputSummary, updateFileInput, updateFileInputSummary]);
  const handleAdditionalFeeTypeChanged = () => {
    const newType = tmpAdditionalFee.type === DISCOUNT_TYPE.AMOUNT ? DISCOUNT_TYPE.PERCENTAGE : DISCOUNT_TYPE.AMOUNT;
    setTmpAdditionalFee(o => ({
      ...o,
      type: newType
    }));
  };
  const handleDiscountTypeChanged = () => {
    const newType = tmpDiscount.type === DISCOUNT_TYPE.AMOUNT ? DISCOUNT_TYPE.PERCENTAGE : DISCOUNT_TYPE.AMOUNT;
    setTmpDiscount(o => ({
      ...o,
      type: newType
    }));
  };
  const handleAdditionalFeeValueChanged = value => {
    const newValue = isNaN(Number(value)) || Number(value) < 0 ? 0 : Number(value);
    setTmpAdditionalFee(o => ({
      ...o,
      value: newValue
    }));
  };
  const handleDiscountValueChanged = value => {
    const newValue = isNaN(Number(value)) || Number(value) < 0 ? 0 : Number(value);
    setTmpDiscount(o => ({
      ...o,
      value: newValue
    }));
  };
  const handleToggleAdditionalFeeEdit = async () => {
    const newBool = !isEditingAdditionalFee;

    // done editing
    if (!newBool) {
      try {
        if (createFileInput) {
          const discountedFile = applyAdditionalFeeToFile({
            file: createFileInput as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>,
            additionalFee: tmpAdditionalFee as unknown as AdditionalFeeInput
          });
          updateCreateFileInput({
            products: [...(discountedFile.products as unknown as FileProductInput[])]
          });
        }
        if (updateFileInput) {
          const discountedFile = applyAdditionalFeeToFile({
            file: updateFileInput as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>,
            additionalFee: tmpAdditionalFee as unknown as AdditionalFeeInput
          });
          updateUpdateFileInput({
            products: [...(discountedFile.products as unknown as FileProductInput[])]
          });
          if (isUpdateOnAdditionalFeeChange) {
            await updateFile.mutateAsync({
              id: String(query.fileId),
              input: {
                products: discountedFile.products as UpdateFileVariables["input"]["products"]
              }
            });
          }
        }
      } catch (e) {
        console.log("failed to update additional fee", e);
      }
    }
    setIsEditingAdditionalFee(newBool);
  };
  const handleToggleDiscountEdit = async () => {
    const newBool = !isEditingDiscount;

    // done editing
    if (!newBool) {
      try {
        if (createFileInput) {
          const discountedFile = applyFixDiscountToFile({
            file: createFileInput as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>,
            fixDiscount: tmpDiscount as unknown as FixDiscountInput
          });
          updateCreateFileInput({
            products: [...(discountedFile.products as unknown as FileProductInput[])]
          });
        }
        if (updateFileInput) {
          const discountedFile = applyFixDiscountToFile({
            file: updateFileInput as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>,
            fixDiscount: tmpDiscount as unknown as FixDiscountInput
          });
          updateUpdateFileInput({
            products: [...(discountedFile.products as unknown as FileProductInput[])]
          });
          if (isUpdateOnDiscountChange) {
            await updateFile.mutateAsync({
              id: String(query.fileId),
              input: {
                products: discountedFile.products as UpdateFileVariables["input"]["products"]
              }
            });
          }
        }
      } catch (e) {
        console.log("failed to update discount", e);
      }
    }
    setIsEditingDiscount(newBool);
  };
  const handleAddProduct = (id: string, qty?: number) => {
    if (createFileInput) {
      updateCreateFileInput(calculateFile({
        ...createFileInput,
        products: [...createFileInput.products.map(p => p._id === id ? {
          ...p,
          quantity: p.quantity + (qty ? qty : 1)
        } : p)]
      } as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>).$file as unknown as CreateFileInput);
    }
    if (updateFileInput) {
      updateUpdateFileInput(calculateFile({
        ...updateFileInput,
        products: [...updateFileInput.products.map(p => p._id === id ? {
          ...p,
          quantity: p.quantity + (qty ? qty : 1)
        } : p)]
      } as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>).$file as unknown as CreateFileInput);
      setTillIsEditFileInput(true);
    }
  };
  const handleRemoveProduct = (id: string, isRemoveAll?: boolean) => {
    if (createFileInput) {
      updateCreateFileInput(calculateFile({
        ...createFileInput,
        products: [...createFileInput.products.map(p => p._id === id ? {
          ...p,
          quantity: isRemoveAll ? 0 : p.quantity - 1,
          updatedAt: new Date().toISOString()
        } : p).filter(p => p.quantity > 0)]
      } as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>).$file as unknown as CreateFileInput);
    }
    if (updateFileInput) {
      const isAdded = itemsDisplay.add.find(i => i.id === id);
      if (isAdded) {
        updateUpdateFileInput(calculateFile({
          ...updateFileInput,
          products: [...updateFileInput.products.map(p => p._id === id ? {
            ...p,
            quantity: isRemoveAll ? 0 : p.quantity - 1,
            updatedAt: new Date().toISOString()
          } : p).filter(p => p.quantity > 0)]
        } as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>).$file as unknown as CreateFileInput);
      } else {
        updateUpdateFileInput(calculateFile({
          ...updateFileInput,
          products: [...updateFileInput.products.map(p => p._id === id ? {
            ...p,
            quantity: isRemoveAll ? 0 : p.quantity - 1,
            updatedAt: new Date().toISOString()
          } : p)]
        } as unknown as FileInputLike<FileInputLike, FileProductInputLike, FileProductOptionInputLike>).$file as unknown as CreateFileInput);
      }
      setTillIsEditFileInput(true);
    }
  };
  const getOrderCurrency = () => {
    if (createFileInput) {
      const config = createFileInput.multiCurrencyConfig;
      if (!config) return DEFAULT_CURRENCY_CODE;
      return config.selectedCurrency;
    }
    if (updateFileInput) {
      const config = updateFileInput.multiCurrencyConfig;
      if (!config) return DEFAULT_CURRENCY_CODE;
      return config.selectedCurrency;
    }
    return DEFAULT_CURRENCY_CODE;
  };
  const getOrderCurrencyDisplay = () => {
    return company?.isMultiCurrencyEnabled ? ` (${getOrderCurrency()})` : "";
  };
  return <Table sx={{
    "& td, & th": {
      borderBottom: "none"
    },
    ...(columnVisibility ? {
      "& th:nth-of-type(1), & tbody tr td:nth-of-type(1)": {
        display: columnVisibility.number === false ? "none" : undefined
      },
      "& th:nth-of-type(2), & tbody tr td:nth-of-type(2)": {
        display: columnVisibility.quantity === false ? "none" : undefined
      },
      "& th:nth-of-type(3), & tbody tr td:nth-of-type(3)": {
        display: columnVisibility.description === false ? "none" : undefined
      },
      "& th:nth-of-type(4), & tbody tr td:nth-of-type(4)": {
        display: columnVisibility.total === false ? "none" : undefined
      }
    } : {})
  }} data-sentry-element="Table" data-sentry-component="ProductsTableSummary" data-sentry-source-file="products-table-summary.tsx">
			<TableHead data-sentry-element="TableHead" data-sentry-source-file="products-table-summary.tsx">
				<TableRow data-sentry-element="TableRow" data-sentry-source-file="products-table-summary.tsx">
					<TableCell data-sentry-element="TableCell" data-sentry-source-file="products-table-summary.tsx">#</TableCell>
					{isAddProduct && <TableCell></TableCell>}
					<TableCell data-sentry-element="TableCell" data-sentry-source-file="products-table-summary.tsx">Qty</TableCell>
					<TableCell data-sentry-element="TableCell" data-sentry-source-file="products-table-summary.tsx">Description</TableCell>
					{isAddProduct && <TableCell></TableCell>}
					<TableCell align="right" data-sentry-element="TableCell" data-sentry-source-file="products-table-summary.tsx">Total{getOrderCurrencyDisplay()}</TableCell>
				</TableRow>
			</TableHead>
			<TableBody data-sentry-element="TableBody" data-sentry-source-file="products-table-summary.tsx">
				{itemsDisplay.add.length > 0 && <Fragment key="added-item-section">
						<TableRow>
							<TableCell colSpan={5} sx={{
            p: 0
          }}>
								<Divider>
									<Typography variant="body2">New Products</Typography>
								</Divider>
							</TableCell>
						</TableRow>
						{itemsDisplay.add.map((item, index) => <Fragment key={item.id}>
								<TableRow hover={!!onRowClick} sx={{
            cursor: onRowClick ? "pointer" : "unset"
          }}>
									<TableCell onClick={() => onRowClick?.(item)}>
										{index + 1}
									</TableCell>
									{isAddProduct && <TableCell>
											<IconButton color="primary" onClick={() => handleAddProduct(item.id)}>
												<SvgIcon>
													<PlusIcon />
												</SvgIcon>
											</IconButton>
										</TableCell>}
									<TableCell>
										<b>{item.quantity}x</b>
									</TableCell>
									<TableCell onClick={() => onRowClick?.(item)}>
										{item.name}
										<br />
										{item.options.map(option => <Typography variant="caption" component="div" key={option.id} color="text.secondary">
												{option.quantity}x {option.name}
											</Typography>)}
										{item.subtotalAddons && !isAllApplyFixeDiscount && <>
												{item.subtotalAddons.fixDiscount.amount > 0 && <Typography variant="body2" component="div" key={`discount-item-${index}`} sx={{
                  fontStyle: "italic",
                  fontWeight: "bold"
                }}>
														{item.subtotalAddons.fixDiscount.fixDiscount ? fixDiscounts.find(d => d._id === item.subtotalAddons.fixDiscount.fixDiscount)?.name : `${item.subtotalAddons.fixDiscount.type === DISCOUNT_TYPE.AMOUNT ? `"$ ${item.subtotalAddons.fixDiscount.amount}" discount` : `"${item.subtotalAddons.fixDiscount.value} %" discount`}`}
													</Typography>}
											</>}
										{item.subtotalAddons && !isAllApplyAdditionalFee && <>
												{item.subtotalAddons.additionalFee.amount > 0 && <Typography variant="body2" component="div" key={`additional-fee-item-${index}`} sx={{
                  fontStyle: "italic",
                  fontWeight: "bold"
                }}>
														{item.subtotalAddons.additionalFee.additionalFee ? additionalFees.find(d => d._id === item.subtotalAddons.additionalFee.additionalFee)?.name : `${item.subtotalAddons.additionalFee.type === DISCOUNT_TYPE.AMOUNT ? `"$ ${item.subtotalAddons.additionalFee.amount}" added` : `"${item.subtotalAddons.additionalFee.value} %" added`}`}
													</Typography>}
											</>}
									</TableCell>
									{isAddProduct && <TableCell>
											<IconButton color="error" disabled={(items.find(i => i.id === item.id)?.quantity ?? 1) < 1} onClick={() => handleRemoveProduct(item.id)}>
												<SvgIcon>
													<MinusIcon />
												</SvgIcon>
											</IconButton>
											<IconButton color="error" disabled={(items.find(i => i.id === item.id)?.quantity ?? 1) < 1} onClick={() => handleRemoveProduct(item.id, true)}>
												<SvgIcon>
													<Trash03Icon />
												</SvgIcon>
											</IconButton>
										</TableCell>}
									<TableCell align="right" onClick={() => onRowClick?.(item)}>
										<Typography key={`${item.id}-subtotal`} color="success">
											{$d(item.subtotal, getOrderCurrency())}
										</Typography>
										{item.subtotalAddons && !isAllApplyFixeDiscount && <>
												{item.subtotalAddons.fixDiscount.amount > 0 && <Typography key={`${item.id}-fixDiscount`} color="error">
														-{" "}
														{$d(item.subtotalAddons.fixDiscount.amount, getOrderCurrency())}
													</Typography>}
											</>}
										{item.subtotalAddons && !isAllApplyAdditionalFee && <>
												{item.subtotalAddons.additionalFee.amount > 0 && <Typography key={`${item.id}-additionalFee`} color="success">
														+{" "}
														{$d(item.subtotalAddons.additionalFee.amount, getOrderCurrency())}
													</Typography>}
											</>}
									</TableCell>
								</TableRow>
							</Fragment>)}
					</Fragment>}
				<Fragment key="previous-item-section">
					<TableRow data-sentry-element="TableRow" data-sentry-source-file="products-table-summary.tsx">
						<TableCell colSpan={5} sx={{
            p: 0
          }} data-sentry-element="TableCell" data-sentry-source-file="products-table-summary.tsx">
							<Divider data-sentry-element="Divider" data-sentry-source-file="products-table-summary.tsx">
								<Typography variant="body2" data-sentry-element="Typography" data-sentry-source-file="products-table-summary.tsx">Current Products</Typography>
							</Divider>
						</TableCell>
					</TableRow>
				</Fragment>
				{itemsDisplay.prev.map((item, index) => <Fragment key={item.id}>
						<TableRow hover={!!onRowClick} sx={{
          cursor: onRowClick ? "pointer" : "unset"
        }}>
							<TableCell onClick={() => onRowClick?.(item)}>
								{index + 1}
							</TableCell>
							{isAddProduct && <TableCell>
									<IconButton color="primary" onClick={() => handleAddProduct(item.id)}>
										<SvgIcon>
											<PlusIcon />
										</SvgIcon>
									</IconButton>
								</TableCell>}
							<TableCell>
								<b>{item.quantity}x</b>
							</TableCell>
							<TableCell onClick={() => onRowClick?.(item)}>
								{item.name}
								<br />
								{item.options.map(option => <Typography variant="caption" component="div" key={option.id} color="text.secondary">
										{option.quantity}x {option.name}
									</Typography>)}
								{item.subtotalAddons && !isAllApplyFixeDiscount && <>
										{item.subtotalAddons.fixDiscount.amount > 0 && <Typography variant="body2" component="div" key={`discount-item-${index}`} sx={{
                fontStyle: "italic",
                fontWeight: "bold"
              }}>
												{item.subtotalAddons.fixDiscount.fixDiscount ? fixDiscounts.find(d => d._id === item.subtotalAddons.fixDiscount.fixDiscount)?.name : `${item.subtotalAddons.fixDiscount.type === DISCOUNT_TYPE.AMOUNT ? `"$ ${item.subtotalAddons.fixDiscount.amount}" discount` : `"${item.subtotalAddons.fixDiscount.value} %" discount`}`}
											</Typography>}
									</>}
								{item.subtotalAddons && !isAllApplyAdditionalFee && <>
										{item.subtotalAddons.additionalFee.amount > 0 && <Typography variant="body2" component="div" key={`additional-fee-item-${index}`} sx={{
                fontStyle: "italic",
                fontWeight: "bold"
              }}>
												{item.subtotalAddons.additionalFee.additionalFee ? additionalFees.find(d => d._id === item.subtotalAddons.additionalFee.additionalFee)?.name : `${item.subtotalAddons.additionalFee.type === DISCOUNT_TYPE.AMOUNT ? `"$ ${item.subtotalAddons.additionalFee.amount}" added` : `"${item.subtotalAddons.additionalFee.value} %" added`}`}
											</Typography>}
									</>}
								{item.updatedAt && <Typography variant="caption" component="div" key={`product-updated-${item.id}`} color="text.secondary">
										&#8226;{" "}
										{$dayjs(item?.updatedAt).format(DATETIMESTRING_FORMAT)}
									</Typography>}
							</TableCell>
							{isAddProduct && <TableCell>
									<IconButton color="error" disabled={(items.find(i => i.id === item.id)?.quantity ?? 1) < 1} onClick={() => handleRemoveProduct(item.id)}>
										<SvgIcon>
											<MinusIcon />
										</SvgIcon>
									</IconButton>
									<IconButton color="error" disabled={(items.find(i => i.id === item.id)?.quantity ?? 1) < 1} onClick={() => handleRemoveProduct(item.id, true)}>
										<SvgIcon>
											<Trash03Icon />
										</SvgIcon>
									</IconButton>
								</TableCell>}
							<TableCell align="right" onClick={() => onRowClick?.(item)}>
								<Typography key={`${item.id}-subtotal`} color="success">
									{$d(item.subtotal, getOrderCurrency())}
								</Typography>
								{item.subtotalAddons && !isAllApplyFixeDiscount && <>
										{item.subtotalAddons.fixDiscount.amount > 0 && <Typography key={`${item.id}-fixDiscount`} color="error">
												-{" "}
												{$d(item.subtotalAddons.fixDiscount.amount, getOrderCurrency())}
											</Typography>}
									</>}
								{item.subtotalAddons && !isAllApplyAdditionalFee && <>
										{item.subtotalAddons.additionalFee.amount > 0 && <Typography key={`${item.id}-additionalFee`} color="success">
												+{" "}
												{$d(item.subtotalAddons.additionalFee.amount, getOrderCurrency())}
											</Typography>}
									</>}
							</TableCell>
						</TableRow>
					</Fragment>)}
				{itemsDisplay.remove.length > 0 && <Fragment key="removed-item-section">
						<TableRow>
							<TableCell colSpan={5} sx={{
            p: 0
          }}>
								<Divider>
									<Typography variant="body2"> Removed Items</Typography>
								</Divider>
							</TableCell>
						</TableRow>
						{itemsDisplay.remove.map((item, index) => <Fragment key={item.id}>
								<TableRow hover={!!onRowClick} sx={{
            cursor: onRowClick ? "pointer" : "unset"
          }}>
									<TableCell onClick={() => onRowClick?.(item)}>
										{index + 1}
									</TableCell>
									{isAddProduct && <TableCell>
											<IconButton color="primary" disabled={(items.find(i => i.id === item.id)?.quantity ?? 1) < 1} onClick={() => handleRemoveProduct(item.id)}>
												<SvgIcon>
													<PlusIcon />
												</SvgIcon>
											</IconButton>
										</TableCell>}
									<TableCell>
										<b>{item.quantity}x</b>
									</TableCell>
									<TableCell onClick={() => onRowClick?.(item)}>
										{item.name}
										<br />
										{item.options.map(option => <Typography variant="caption" component="div" key={option.id} color="text.secondary">
												{option.quantity}x {option.name}
											</Typography>)}
										{item.subtotalAddons && !isAllApplyFixeDiscount && <>
												{item.subtotalAddons.fixDiscount.amount > 0 && <Typography variant="body2" component="div" key={`discount-item-${index}`} sx={{
                  fontStyle: "italic",
                  fontWeight: "bold"
                }}>
														{item.subtotalAddons.fixDiscount.fixDiscount ? fixDiscounts.find(d => d._id === item.subtotalAddons.fixDiscount.fixDiscount)?.name : `${item.subtotalAddons.fixDiscount.type === DISCOUNT_TYPE.AMOUNT ? `"$ ${item.subtotalAddons.fixDiscount.amount}" discount` : `"${item.subtotalAddons.fixDiscount.value} %" discount`}`}
													</Typography>}
											</>}
										{item.subtotalAddons && !isAllApplyAdditionalFee && <>
												{item.subtotalAddons.additionalFee.amount > 0 && <Typography variant="body2" component="div" key={`additional-fee-item-${index}`} sx={{
                  fontStyle: "italic",
                  fontWeight: "bold"
                }}>
														{item.subtotalAddons.additionalFee.additionalFee ? additionalFees.find(d => d._id === item.subtotalAddons.additionalFee.additionalFee)?.name : `${item.subtotalAddons.additionalFee.type === DISCOUNT_TYPE.AMOUNT ? `"$ ${item.subtotalAddons.additionalFee.amount}" added` : `"${item.subtotalAddons.additionalFee.value} %" added`}`}
													</Typography>}
											</>}
									</TableCell>
									{isAddProduct && <TableCell>
											<IconButton color="error" onClick={() => handleAddProduct(item.id)}>
												<SvgIcon>
													<MinusIcon />
												</SvgIcon>
											</IconButton>
											<IconButton color="error" onClick={() => handleAddProduct(item.id, item.quantity)}>
												<SvgIcon>
													<Trash03Icon />
												</SvgIcon>
											</IconButton>
										</TableCell>}
									<TableCell align="right" onClick={() => onRowClick?.(item)}>
										<Typography key={`${item.id}-subtotal`} color="success">
											{$d(item.subtotal, getOrderCurrency())}
										</Typography>
										{item.subtotalAddons && !isAllApplyFixeDiscount && <>
												{item.subtotalAddons.fixDiscount.amount > 0 && <Typography key={`${item.id}-fixDiscount`} color="error">
														-{" "}
														{$d(item.subtotalAddons.fixDiscount.amount, getOrderCurrency())}
													</Typography>}
											</>}
										{item.subtotalAddons && !isAllApplyAdditionalFee && <>
												{item.subtotalAddons.additionalFee.amount > 0 && <Typography key={`${item.id}-additionalFee`} color="success">
														+{" "}
														{$d(item.subtotalAddons.additionalFee.amount, getOrderCurrency())}
													</Typography>}
											</>}
									</TableCell>
								</TableRow>
							</Fragment>)}
					</Fragment>}
				<TableRow data-sentry-element="TableRow" data-sentry-source-file="products-table-summary.tsx">
					<TableCell colSpan={5} sx={{
          p: 0
        }} data-sentry-element="TableCell" data-sentry-source-file="products-table-summary.tsx">
						<Divider data-sentry-element="Divider" data-sentry-source-file="products-table-summary.tsx" />
					</TableCell>
				</TableRow>
				<TableRow data-sentry-element="TableRow" data-sentry-source-file="products-table-summary.tsx">
					{isAddProduct && <TableCell colSpan={1} sx={{
          borderBottom: "none"
        }} />}
					<TableCell colSpan={3} align="right" sx={{
          borderBottom: "none"
        }} data-sentry-element="TableCell" data-sentry-source-file="products-table-summary.tsx">
						<Typography variant="subtitle1" data-sentry-element="Typography" data-sentry-source-file="products-table-summary.tsx">Sub total</Typography>
					</TableCell>
					{isAddProduct && <TableCell colSpan={1} sx={{
          borderBottom: "none"
        }} />}
					<TableCell align="right" sx={{
          borderBottom: "none"
        }} data-sentry-element="TableCell" data-sentry-source-file="products-table-summary.tsx">
						<Typography variant="subtitle2" data-sentry-element="Typography" data-sentry-source-file="products-table-summary.tsx">
							{$d(subtotal, getOrderCurrency())}
						</Typography>
					</TableCell>
				</TableRow>
				<TableRow data-sentry-element="TableRow" data-sentry-source-file="products-table-summary.tsx">
					{isAddProduct && <TableCell colSpan={1} sx={{
          borderBottom: "none"
        }} />}
					<TableCell colSpan={3} align="right" sx={{
          borderBottom: "none"
        }} data-sentry-element="TableCell" data-sentry-source-file="products-table-summary.tsx">
						<Typography variant="subtitle1" data-sentry-element="Typography" data-sentry-source-file="products-table-summary.tsx">
							Fix discount
							{isAllApplyFixeDiscount ? `: "${items[0].subtotalAddons.fixDiscount.fixDiscount ? fixDiscounts.find(d => d._id === items[0].subtotalAddons.fixDiscount.fixDiscount)?.name : items[0].subtotalAddons.fixDiscount.type === DISCOUNT_TYPE.AMOUNT ? `$ ${items[0].subtotalAddons.fixDiscount.value} discount` : `${items[0].subtotalAddons.fixDiscount.value} % discount`}"` : ""}
						</Typography>
					</TableCell>
					{isAddProduct && <TableCell colSpan={1} sx={{
          borderBottom: "none"
        }} />}
					<TableCell align="right" sx={{
          borderBottom: "none"
        }} data-sentry-element="TableCell" data-sentry-source-file="products-table-summary.tsx">
						{isEditingDiscount ? <>
								<IconButton disabled={!items.length} color="primary" onClick={handleToggleDiscountEdit}>
									{isEditingDiscount ? <CheckOutlined /> : <EditOutlined />}
								</IconButton>
								<TextField key={tmpDiscount.value} type="number" defaultValue={tmpDiscount.value} sx={{
              width: "150px",
              "& .MuiOutlinedInput-root": {
                pl: 0
              },
              "& input": {
                textAlign: "right"
              }
            }} InputProps={{
              startAdornment: <InputAdornment position="start">
												<IconButton color="primary" onClick={handleDiscountTypeChanged}>
													<Typography variant="body1">
														{tmpDiscount.type === DISCOUNT_TYPE.AMOUNT ? "$" : "%"}
													</Typography>
												</IconButton>
											</InputAdornment>
            }} onFocus={e => {
              if (Number(e.target.value) === 0) e.target.value = "";
            }} onBlur={e => handleDiscountValueChanged(e.target.value)} />
							</> : <Typography variant="subtitle2">
								<IconButton disabled={!items.length} color="primary" onClick={handleToggleDiscountEdit}>
									{isEditingDiscount ? <CheckOutlined /> : <EditOutlined />}
								</IconButton>
								{$d(fixDiscount, getOrderCurrency())}
							</Typography>}
					</TableCell>
				</TableRow>
				<TableRow data-sentry-element="TableRow" data-sentry-source-file="products-table-summary.tsx">
					{isAddProduct && <TableCell colSpan={1} sx={{
          borderBottom: "none"
        }} />}
					<TableCell colSpan={3} align="right" sx={{
          borderBottom: "none"
        }} data-sentry-element="TableCell" data-sentry-source-file="products-table-summary.tsx">
						<Typography variant="subtitle1" data-sentry-element="Typography" data-sentry-source-file="products-table-summary.tsx">
							Additional Fee
							{isAllApplyAdditionalFee ? `: "${items[0].subtotalAddons.additionalFee.additionalFee ? additionalFees.find(d => d._id === items[0].subtotalAddons.additionalFee.additionalFee)?.name : items[0].subtotalAddons.additionalFee.type === DISCOUNT_TYPE.AMOUNT ? `$ ${items[0].subtotalAddons.additionalFee.value} added` : `${items[0].subtotalAddons.additionalFee.value} % added`}"` : ""}
						</Typography>
					</TableCell>
					{isAddProduct && <TableCell colSpan={1} sx={{
          borderBottom: "none"
        }} />}
					<TableCell align="right" sx={{
          borderBottom: "none"
        }} data-sentry-element="TableCell" data-sentry-source-file="products-table-summary.tsx">
						{isEditingAdditionalFee ? <>
								<IconButton disabled={!items.length} color="primary" onClick={handleToggleAdditionalFeeEdit}>
									{isEditingAdditionalFee ? <CheckOutlined /> : <EditOutlined />}
								</IconButton>
								<TextField key={tmpAdditionalFee.value} type="number" defaultValue={tmpAdditionalFee.value} sx={{
              width: "150px",
              "& .MuiOutlinedInput-root": {
                pl: 0
              },
              "& input": {
                textAlign: "right"
              }
            }} InputProps={{
              startAdornment: <InputAdornment position="start">
												<IconButton color="primary" onClick={handleAdditionalFeeTypeChanged}>
													<Typography variant="body1">
														{tmpAdditionalFee.type === DISCOUNT_TYPE.AMOUNT ? "$" : "%"}
													</Typography>
												</IconButton>
											</InputAdornment>
            }} onFocus={e => {
              if (Number(e.target.value) === 0) e.target.value = "";
            }} onBlur={e => handleAdditionalFeeValueChanged(e.target.value)} />
							</> : <Typography variant="subtitle2">
								<IconButton disabled={!items.length} color="primary" onClick={handleToggleAdditionalFeeEdit}>
									{isEditingAdditionalFee ? <CheckOutlined /> : <EditOutlined />}
								</IconButton>
								{$d(additionalFee, getOrderCurrency())}
							</Typography>}
					</TableCell>
				</TableRow>
				<TableRow data-sentry-element="TableRow" data-sentry-source-file="products-table-summary.tsx">
					{isAddProduct && <TableCell colSpan={1} sx={{
          borderBottom: "none"
        }} />}
					<TableCell colSpan={3} align="right" sx={{
          borderBottom: "none"
        }} data-sentry-element="TableCell" data-sentry-source-file="products-table-summary.tsx">
						<Typography variant="subtitle1" data-sentry-element="Typography" data-sentry-source-file="products-table-summary.tsx">
							Grand total{getOrderCurrencyDisplay()}
						</Typography>
					</TableCell>
					{isAddProduct && <TableCell colSpan={1} sx={{
          borderBottom: "none"
        }} />}
					<TableCell align="right" sx={{
          borderBottom: "none"
        }} data-sentry-element="TableCell" data-sentry-source-file="products-table-summary.tsx">
						<Typography variant="subtitle2" data-sentry-element="Typography" data-sentry-source-file="products-table-summary.tsx">
							{$d(grandTotal, getOrderCurrency())}
						</Typography>
					</TableCell>
				</TableRow>
			</TableBody>
		</Table>;
};